/**
 * Sync the thumbnail strip on every variant change, valid or not.
 *
 * Dawn's product-info.js updateMedia() only ever diffs/reorders ONE media
 * list: `this.querySelector('media-gallery ul')`, which - since querySelector
 * returns the first match - is always the main Slider-Gallery list. The
 * second list, Slider-Thumbnails, is never touched by that function at all.
 * Stock Dawn never surfaced this because every color rendered the exact same
 * 24 media ids (nothing was ever filtered out), so there was nothing to
 * actually add/remove/reorder - the thumbnail strip already matched by
 * coincidence. Now that the gallery is filtered by color (Color Filter
 * mode, per BRIEF TÉCNICO FINAL — GALERÍA DINÁMICA POR PRODUCTO), each color
 * has a genuinely different id set, so the thumbnail strip is left showing
 * the previous color's photos after every change.
 *
 * Worse: MediaGallery.setActiveMedia() (assets/media-gallery.js) then tries
 * to mark the new variant's featured photo active inside that same stale
 * thumbnail strip - `this.elements.thumbnails.querySelector('[data-target="..."]')`
 * - finds nothing, and `activeThumbnail.parentElement` throws on the null
 * result. That exception aborts the rest of Dawn's own update function
 * before it reaches the code that re-enables the "Add to cart" button,
 * which is why the button was intermittently stuck disabled after a color
 * change - confirmed via the exact console stack trace during testing.
 *
 * The fix below wraps updateMedia() to sync the thumbnail list (keyed off
 * its real data-target attribute, not data-media-id) before calling Dawn's
 * original implementation. Once the thumbnail strip actually contains the
 * new variant's photo, setActiveMedia() finds it normally and nothing
 * throws - fixing the stale thumbnails and the stuck Add to cart button
 * from the same root cause. Nothing about cart, inventory, or the
 * add-to-cart/buy-now buttons themselves is touched; this only wraps the
 * media sync step that runs before Dawn's own button-enabling code.
 *
 * SAME PATCH ALSO KEEPS THE GALLERY POSITION (BRIEF #10-#12, #31-#32):
 * stock Dawn always jumps the gallery to the new variant's
 * featured/primary photo after ANY variant change, color or size.
 * Per the brief, a Size change that doesn't change the color group should
 * never move the gallery, and a Color change should land on the SAME
 * local position in the new group when it exists there, or on
 * `Math.min(currentPosition, newGroupSize)` when the new group has fewer
 * photos - never resetting to position 1 unless no valid position exists.
 *
 * This ONLY applies to products actually running Color Filter mode for
 * the variant being rendered (gallery-mode = "Color Filter" AND a
 * matching Gallery Map group was found server-side - see
 * product-media-gallery.liquid). For "Variant Hero" products, or a
 * Color Filter product whose Gallery Map doesn't cover the selected
 * combination, Dawn's own native hero-switching behavior runs completely
 * unmodified, exactly as the brief requires. Whether Color Filter is
 * active for the CURRENT render is read straight off the freshly-fetched
 * HTML's own <media-gallery data-gallery-color-filter="true|false">
 * attribute (set server-side per request), not off the live, not-yet-
 * updated DOM - Color Filter coverage can legitimately differ from one
 * selected color to the next if the Gallery Map only covers some of a
 * product's colors, and reading the live DOM would report last render's
 * value instead of the one that matters for this update.
 *
 * Position is tracked via each thumbnail's server-rendered
 * `data-media-position` attribute (set in product-media-gallery.liquid),
 * NOT via plain DOM order in the list - see the bug entries below for why
 * DOM order doesn't survive repeated changes. In Color Filter mode this
 * attribute is a simple 1..N index within the CURRENT color's own
 * mediaIds order (the brief's "local position").
 *
 * BUG #1 FOUND IN TESTING: the position lookup was originally done AFTER
 * calling originalUpdateMedia, but that call ends by invoking Dawn's own
 * setActiveMedia(variantFeaturedMediaId, true), which - since prepend is
 * true - physically MOVES the variant's featured-photo thumbnail to the
 * FRONT of the list as a side effect. Reading a position after that point
 * meant reading an already-reordered list, landing on the wrong photo.
 * Fixed by resolving which thumbnail occupies the target position right
 * after syncMediaList, BEFORE calling originalUpdateMedia at all.
 *
 * BUG #2 FOUND IN TESTING (the fix above only survived ONE change, then
 * reverted to the primary photo on the next one): plain DOM-order index
 * was originally used for the position key. syncMediaList's own last step
 * explicitly re-sorts the thumbnail list to match the freshly fetched
 * HTML's natural order every time it runs - which silently undoes
 * whatever position bug #1's fix had promoted the previous photo to
 * (index 0, since setActiveMedia's prepend moves the active item to the
 * front). `data-media-position`, being a data attribute that travels WITH
 * its element regardless of DOM position, has no such problem.
 *
 * BUG #3 FOUND IN TESTING (regression reported after #1/#2 shipped): even
 * with the correct target thumbnail resolved, the active photo still
 * jumped to the FRONT of the thumbnail rail on every color change. Root
 * cause: setActiveMedia(mediaId, prepend) in media-gallery.js, when
 * prepend is true, physically calls `.prepend()` on BOTH the active main
 * slide AND its matching thumbnail - it doesn't just scroll to them, it
 * moves them in the DOM. Two different calls were doing this: Dawn's OWN
 * originalUpdateMedia ends by calling
 * setActiveMedia(variantFeaturedMediaId, true) internally (a call this
 * file only wraps, never reads the body of), and then this file's own
 * follow-up call was ALSO passing true. Per the brief, Color Filter's
 * thumbnail rail order must stay exactly gallery_map.groups[].mediaIds,
 * forever - "activating" a photo must never move anything, only mark it
 * active and scroll it into view.
 *
 * Fixed two ways below: (1) while Color Filter is active, this file's own
 * follow-up call now passes prepend=false - scrollIntoView semantics,
 * zero reordering, the exact same code path Dawn already uses for a
 * manual thumbnail click (see the click-delegation listener further
 * down, which has always passed false). (2) Dawn's OWN internal call
 * happens inside originalUpdateMedia, a function this file only calls
 * and never reads the source of, so the only reliable way to stop IT
 * from reordering is to shadow this specific <media-gallery> instance's
 * setActiveMedia for the duration of that one call, forcing every
 * invocation - Dawn's included - to run with prepend=false, then restore
 * the real method immediately after. resetPages() (pagination/prev-next
 * bookkeeping on the main slider) is normally a side effect of
 * prepend=true, so it's called explicitly instead - it only recomputes
 * slide counts/offsets and doesn't care which slide is "active" or what
 * order they're in, so it's safe to call on its own.
 *
 * REVERSED DECISION (kept for history): an earlier version only preserved
 * position for Size changes, always resetting on Color changes, and
 * separately gated preservation on the new variant being in stock. Both
 * were later corrected by explicit user/brief direction: a sold-out size
 * is still just a size change (photo stays put), and - per this brief -
 * a Color change should ALSO keep the equivalent position whenever the
 * new color's own group has enough photos, only falling back toward
 * position 1 via Math.min when it doesn't. The gating that matters now is
 * purely "is Color Filter active for this render", not which option
 * changed.
 */

// Shared by both the updateMedia patch below and the unavailable-combo
// fallback further down this file - both need to know, for the specific
// freshly-fetched HTML they're each handling, whether THAT render has
// Color Filter mode active (see product-media-gallery.liquid's
// data-gallery-color-filter attribute, set server-side per request).
function isColorFilterActive(html) {
  var mediaGallery = html.querySelector('media-gallery');
  return !!(mediaGallery && mediaGallery.dataset.galleryColorFilter === 'true');
}

(function () {
  function syncMediaList(sourceList, destinationList, attr) {
    if (!sourceList || !destinationList) return;
    var selector = '[' + attr + ']';
    var sourceItems = Array.from(sourceList.querySelectorAll(selector));
    var sourceSet = new Set(sourceItems.map(function (item) { return item.getAttribute(attr); }));
    var destinationItems = Array.from(destinationList.querySelectorAll(selector));
    var destinationSet = new Set(destinationItems.map(function (item) { return item.getAttribute(attr); }));

    for (var i = destinationItems.length - 1; i >= 0; i--) {
      if (!sourceSet.has(destinationItems[i].getAttribute(attr))) {
        sourceList.prepend(destinationItems[i]);
      }
    }

    for (var j = 0; j < sourceItems.length; j++) {
      if (sourceItems[j].isConnected && !destinationSet.has(sourceItems[j].getAttribute(attr))) {
        sourceItems[j].remove();
      }
    }

    var refreshedSourceItems = Array.from(sourceList.querySelectorAll(selector));
    var sourceMap = new Map(
      refreshedSourceItems.map(function (item, index) {
        return [item.getAttribute(attr), { item: item, index: index }];
      })
    );

    destinationItems.forEach(function (destinationItem, destinationIndex) {
      var sourceData = sourceMap.get(destinationItem.getAttribute(attr));
      if (sourceData && sourceData.index !== destinationIndex) {
        var referenceNode = sourceList.querySelector(selector + ':nth-of-type(' + (destinationIndex + 1) + ')');
        sourceList.insertBefore(sourceData.item, referenceNode);
      }
    });
  }

  customElements.whenDefined('product-info').then(function () {
    var ProductInfo = customElements.get('product-info');
    if (!ProductInfo || !ProductInfo.prototype.updateMedia || ProductInfo.prototype.__thumbnailSyncPatched) return;

    var originalUpdateMedia = ProductInfo.prototype.updateMedia;

    ProductInfo.prototype.updateMedia = function (html, variantFeaturedMediaId) {
      if (!variantFeaturedMediaId) return;

      var thumbsSource = this.querySelector('[id^="Slider-Thumbnails-"]');
      var thumbsDestination = html.querySelector('[id^="Slider-Thumbnails-"]');

      // Capture the currently-active photo's local position BEFORE
      // anything is synced/replaced - only meaningful (and only read) when
      // this specific render is actually running Color Filter mode.
      var preservePosition = isColorFilterActive(html);
      var activeThumbButton = preservePosition
        ? this.querySelector('[id^="Slider-Thumbnails-"] button[aria-current]')
        : null;
      var activeThumbItem = activeThumbButton ? activeThumbButton.closest('[data-target]') : null;
      var targetPositionNum =
        activeThumbItem && activeThumbItem.dataset.mediaPosition
          ? parseInt(activeThumbItem.dataset.mediaPosition, 10)
          : null;

      syncMediaList(thumbsSource, thumbsDestination, 'data-target');

      // Resolve which thumbnail to land on - this MUST happen right here,
      // before originalUpdateMedia runs below. That call ends by invoking
      // Dawn's own setActiveMedia(variantFeaturedMediaId, true), which
      // (with prepend=true) physically moves whichever photo it activates
      // to the front of the list, but that doesn't affect this lookup
      // since it's keyed by data-media-position, not list order.
      //
      // Math.min caps the target to the new group's own size (BRIEF #12):
      // same position when the new color has enough photos, otherwise its
      // own last photo - never position 1 unless nothing else is valid.
      var matchingThumb = null;
      if (targetPositionNum != null && !isNaN(targetPositionNum) && thumbsSource) {
        var newThumbItems = Array.from(thumbsSource.querySelectorAll('[data-target]'));
        var newCount = newThumbItems.length;
        if (newCount > 0) {
          var cappedPosition = Math.min(targetPositionNum, newCount);
          matchingThumb = thumbsSource.querySelector('[data-target][data-media-position="' + cappedPosition + '"]');
        }
      }

      // While Color Filter is active, shadow this instance's setActiveMedia
      // for the duration of originalUpdateMedia's call so ITS internal
      // setActiveMedia(variantFeaturedMediaId, true) - which this file
      // never reads the source of, only calls - can't reorder the rail
      // either (see BUG #3 above). Restored immediately after.
      var mediaGallery = this.querySelector('media-gallery');
      var unshadowSetActiveMedia = null;
      if (preservePosition && mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
        var realSetActiveMedia = mediaGallery.setActiveMedia.bind(mediaGallery);
        mediaGallery.setActiveMedia = function (mediaId) {
          return realSetActiveMedia(mediaId, false);
        };
        unshadowSetActiveMedia = function () {
          delete mediaGallery.setActiveMedia;
        };
      }

      // try/finally: if originalUpdateMedia throws partway through, the
      // shadow above MUST still come off - otherwise this <media-gallery>
      // instance would be left forever unable to reorder (prepend=true)
      // even for a later Variant Hero render, past the point where
      // preservePosition/Color Filter is even still relevant.
      var result;
      try {
        result = originalUpdateMedia.call(this, html, variantFeaturedMediaId);
      } finally {
        if (unshadowSetActiveMedia) unshadowSetActiveMedia();
      }

      if (preservePosition && mediaGallery && mediaGallery.elements && mediaGallery.elements.viewer) {
        if (typeof mediaGallery.elements.viewer.resetPages === 'function') {
          mediaGallery.elements.viewer.resetPages();
        }
      }

      if (matchingThumb && mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
        mediaGallery.setActiveMedia(matchingThumb.dataset.target, false);
      }

      return result;
    };

    ProductInfo.prototype.__thumbnailSyncPatched = true;
  });
})();

/**
 * Color-gallery fallback for unavailable variant combinations.
 *
 * When a shopper picks a color/size combination that doesn't exist at all,
 * Shopify's section render returns a null variant for that exact
 * combination, and Dawn's product-info.js (handleUpdateProductInfo) detects
 * the null variant and returns immediately - it never calls updateMedia()
 * and never publishes PUB_SUB_EVENTS.variantChange. The gallery is simply
 * left showing whatever was on screen before, even though the swatches
 * themselves update correctly.
 *
 * IMPORTANT (discovered via live testing, corrects an earlier assumption in
 * this file): re-requesting the section with ONLY the color's option value
 * (dropping size entirely) does NOT reliably resolve to "first available
 * size in that color" the way selected_or_first_available_variant does on a
 * normal page load. Empirically, Shopify's section-rendering endpoint
 * returns a NULL variant for a partial option_values list whenever more
 * than one variant matches it. So the color-only approach silently failed
 * for every color that has more than one size - i.e. every real case.
 *
 * The fix: collect every Size option value currently rendered in
 * variant-selects (Dawn always renders all of them, styling unavailable
 * ones rather than removing them), and fire one section-render request per
 * candidate size in parallel with the target color's optionValueId.
 * Whichever combination comes back with a real (non-null) variant is the
 * one used to refresh the gallery and thumbnails. This needs no hardcoded
 * knowledge of which sizes exist for which color - it just tries them all
 * and keeps the first hit. Candidates are matched by optionValueId (the
 * same technical key the brief requires for Color Filter itself), never
 * by the option's displayed/translatable name.
 *
 * The listener is delegated on document (not attached directly to each
 * variant-selects element) because Dawn's own updateOptionValues() replaces
 * the whole variant-selects node via HTMLUpdateUtility.viewTransition() on
 * every successful variant change. A listener bound to the original node
 * would go silently dead after the first swap and miss every change after
 * that - confirmed by testing (Black -> 5XL -> Paragon got stuck on Black
 * until the listener was moved to document-level delegation).
 *
 * Position preservation here mirrors the updateMedia patch above (same
 * Math.min-capped local position, same "only when Color Filter is active
 * for the render that comes back" gating) - a size-only change landing on
 * a genuinely nonexistent combo still keeps the color group's own photo
 * position instead of resetting to its first photo.
 */
document.addEventListener('change', function (event) {
  var variantSelects = event.target.closest('variant-selects');
  if (!variantSelects) return;

  var productInfo = variantSelects.closest('product-info');
  if (!productInfo) return;

  var sectionId = productInfo.dataset.section;
  var productUrl = productInfo.dataset.url;
  if (!sectionId || !productUrl) return;

  var colorValueId = null;
  Array.from(variantSelects.querySelectorAll('select option[selected], fieldset input:checked')).forEach(
    function (input) {
      var name = (input.name || '').trim().toLowerCase();
      if (name.indexOf('color') === 0) colorValueId = input.dataset.optionValueId;
    }
  );
  if (!colorValueId) return;

  var otherValueIds = [];
  Array.from(variantSelects.querySelectorAll('fieldset input[type="radio"]')).forEach(function (input) {
    var name = (input.name || '').trim().toLowerCase();
    if (name.indexOf('color') === 0) return;
    if (input.dataset.optionValueId && otherValueIds.indexOf(input.dataset.optionValueId) === -1) {
      otherValueIds.push(input.dataset.optionValueId);
    }
  });

  setTimeout(function () {
    var currentSelects = productInfo.querySelector('variant-selects');
    var selectedVariantEl = currentSelects ? currentSelects.querySelector('[data-selected-variant]') : null;
    var isUnavailable = !selectedVariantEl || selectedVariantEl.textContent.trim() === 'null';
    if (!isUnavailable) return;

    var candidates = otherValueIds.length ? otherValueIds : [null];

    // Capture the current local position now, before any of the async
    // work below touches the DOM. Whether it's actually used depends on
    // which candidate HTML comes back with Color Filter active - decided
    // further down, once we know that.
    var activeThumbButton = document.querySelector(
      'ul[id^="Slider-Thumbnails-' + sectionId + '"] button[aria-current]'
    );
    var activeThumbItem = activeThumbButton ? activeThumbButton.closest('[data-target]') : null;
    var targetPositionNum =
      activeThumbItem && activeThumbItem.dataset.mediaPosition
        ? parseInt(activeThumbItem.dataset.mediaPosition, 10)
        : null;

    Promise.all(
      candidates.map(function (otherId) {
        var optionValues = otherId ? colorValueId + ',' + otherId : colorValueId;
        var url = productUrl + '?section_id=' + sectionId + '&option_values=' + optionValues;

        return fetch(url)
          .then(function (response) {
            return response.text();
          })
          .then(function (responseText) {
            var candidateHtml = new DOMParser().parseFromString(responseText, 'text/html');
            var candidateVariantEl = candidateHtml.querySelector('[data-selected-variant]');
            var candidateIsNull = !candidateVariantEl || candidateVariantEl.textContent.trim() === 'null';
            return candidateIsNull ? null : candidateHtml;
          })
          .catch(function () {
            return null;
          });
      })
    ).then(function (results) {
      var html = results.filter(function (result) {
        return result !== null;
      })[0];
      if (!html) return;

      var gallerySelector = 'ul[id^="Slider-Gallery-' + sectionId + '"]';
      var thumbsSelector = 'ul[id^="Slider-Thumbnails-' + sectionId + '"]';

      var sourceGallery = document.querySelector(gallerySelector);
      var newGallery = html.querySelector(gallerySelector);
      if (sourceGallery && newGallery) {
        sourceGallery.innerHTML = newGallery.innerHTML;
      }

      var sourceThumbs = document.querySelector(thumbsSelector);
      if (sourceThumbs) {
        var newThumbs = html.querySelector(thumbsSelector);
        sourceThumbs.innerHTML = newThumbs ? newThumbs.innerHTML : '';
      }

      var colorFilterActive = isColorFilterActive(html);
      var matchingThumb = null;
      if (colorFilterActive && targetPositionNum != null && !isNaN(targetPositionNum) && sourceThumbs) {
        var newThumbItems = Array.from(sourceThumbs.querySelectorAll('[data-target]'));
        var newCount = newThumbItems.length;
        if (newCount > 0) {
          var cappedPosition = Math.min(targetPositionNum, newCount);
          matchingThumb = sourceThumbs.querySelector('[data-target][data-media-position="' + cappedPosition + '"]');
        }
      }

      var mediaGallery = productInfo.querySelector('media-gallery');

      if (matchingThumb && mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
        // prepend=false: mark active + scroll into view without moving
        // anything in the rail (see BUG #3 above) - this path never runs
        // Dawn's own updateMedia at all (the variant is null), so there's
        // no internal call to worry about neutralizing here, just this one.
        mediaGallery.setActiveMedia(matchingThumb.dataset.target, false);
        if (mediaGallery.elements && mediaGallery.elements.viewer && typeof mediaGallery.elements.viewer.resetPages === 'function') {
          mediaGallery.elements.viewer.resetPages();
        }
      } else {
        var viewer = document.querySelector('#GalleryViewer-' + sectionId);
        if (viewer) {
          if (viewer.slider) viewer.slider.scrollTo({ left: 0 });
          if (typeof viewer.resetPages === 'function') viewer.resetPages();
        }
      }
    });
  }, 700);
});

/**
 * Keep thumbnail clicks working after the thumbnail list has been
 * resynced by the patch above.
 *
 * Dawn's media-gallery.js binds a click listener directly to each
 * thumbnail <button> only once, in its constructor, over whichever
 * thumbnail nodes exist at that moment (page load). The thumbnail-sync
 * patch above adds/removes/reorders thumbnail <li> nodes on every color
 * change (each color has a different photo set) - any newly-inserted
 * node comes straight from the fetched HTML and was never touched by
 * media-gallery.js's constructor, so it never gets a click listener.
 * Result: after switching color at least once, clicking most/all
 * thumbnails silently does nothing - confirmed by testing.
 *
 * Fix: a single delegated listener on document, in the CAPTURE phase so
 * it runs and calls stopPropagation() before the click ever reaches the
 * target button - which both handles every thumbnail (old or freshly
 * synced) uniformly, and prevents Dawn's own per-button listener (on the
 * original, never-replaced thumbnails) from ALSO firing and double-
 * triggering setActiveMedia. media-gallery.js's setActiveMedia() is a
 * public method on the custom element instance itself (the DOM element
 * IS the class instance), so it can be called directly without needing
 * to patch or subclass anything.
 */
document.addEventListener(
  'click',
  function (event) {
    var button = event.target.closest('[id^="Slider-Thumbnails-"] [data-target] button');
    if (!button) return;

    var thumbnailItem = button.closest('[data-target]');
    var mediaGallery = button.closest('media-gallery');
    if (!thumbnailItem || !mediaGallery || typeof mediaGallery.setActiveMedia !== 'function') return;

    event.preventDefault();
    event.stopPropagation();
    mediaGallery.setActiveMedia(thumbnailItem.dataset.target, false);
  },
  true
);

/**
 * Sibling-file injector for everything below.
 *
 * IMPORTANT: this does NOT reuse color-gallery-fix.js's own asset_url
 * (i.e. its CDN version-hash query string) for the sibling files anymore.
 * It originally did (by string-replacing the filename in
 * document.currentScript.src), which meant a sibling file only got a
 * fresh URL - and so only bypassed the browser's HTTP cache - on deploys
 * where color-gallery-fix.js's own bytes ALSO happened to change. A
 * solo edit to e.g. sticky-add-to-cart.js kept the exact same derived
 * URL as before, so browsers kept serving the stale cached copy
 * indefinitely - confirmed by testing (an IntersectionObserver rewrite
 * silently kept running the old code after a solo re-upload of that
 * file). Fixed by giving each sibling file its own explicit "?v=N" that
 * gets bumped by hand whenever that specific file's content changes.
 */
(function () {
  var currentSrc = document.currentScript ? document.currentScript.src : '';
  if (!currentSrc) return;
  var assetsBaseUrl = currentSrc.replace(/color-gallery-fix\.js.*$/, '');
  if (assetsBaseUrl === currentSrc) return;

  function loadCss(filename, version) {
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = assetsBaseUrl + filename + '?v=' + version;
    document.head.appendChild(link);
  }

  function loadScript(filename, version) {
    var script = document.createElement('script');
    script.src = assetsBaseUrl + filename + '?v=' + version;
    script.defer = true;
    document.head.appendChild(script);
  }

  /**
   * Nike-style lateral thumbnails.
   *
   * Repositions the existing native Dawn "Thumbnails" / "Thumbnails, slider"
   * gallery layout (.product--thumbnail / .product--thumbnail_slider) into a
   * vertical column beside the main image on desktop (min-width: 990px)
   * instead of the default strip below it. Pure CSS repositioning of
   * existing elements - no new HTML, no variant/cart logic touched.
   */
  loadCss('gallery-lateral.css', 1);

  /**
   * Add-to-cart button emphasis (unrelated to the color gallery work above -
   * loaded from here purely because this script already runs on every
   * product page, avoiding a separate edit to sections/main-product.liquid
   * just to add one more stylesheet tag).
   *
   * Makes "Add to cart" solid (primary-button style) instead of Dawn's
   * default outline style whenever "Buy with Shop" is also shown, so Add to
   * Cart reads as the primary action rather than losing visual weight to
   * the accelerated checkout button.
   */
  loadCss('add-to-cart-emphasis.css', 1);

  /**
   * Sticky mobile "Add to cart" bar (demo requested by the user).
   *
   * Adds a fixed bottom bar on mobile that appears once the real Add to
   * Cart button has scrolled out of view. See sticky-add-to-cart.js for
   * the full behavior/rationale.
   */
  loadCss('sticky-add-to-cart.css', 3);
  loadScript('sticky-add-to-cart.js', 2);

  /**
   * Price-change awareness (brief highlight pulse when the price actually
   * changes value, e.g. switching to a size with a different price - this
   * store has real price variance by size on several products). See
   * assets/price-update-highlight.js for the full rationale.
   */
  loadCss('price-update-highlight.css', 1);
  loadScript('price-update-highlight.js', 1);

  /**
   * Mobile-only reorder: Title + Price above the photo gallery (demo
   * requested by the user, Nike-style order). See
   * assets/mobile-title-price-reorder.js for the full rationale - it
   * physically relocates the real title/price elements rather than
   * duplicating them, so Dawn's own price-update-on-variant-change code
   * keeps working unmodified.
   */
  loadCss('mobile-title-price-reorder.css', 3);
  loadScript('mobile-title-price-reorder.js', 3);

  /**
   * "Desde $X" price-range display (requested by the user for products
   * with real price variance by option), shown only until the shopper has
   * made one deliberate choice on any option. See
   * assets/price-range-display.js for the full rationale - it reads
   * every variant's real price via {productUrl}.js and only overrides
   * the displayed text, so it doesn't touch price calculation, variant
   * selection, or cart logic.
   */
  loadScript('price-range-display.js', 6);

  // gcw-hide-one-variant.css (hides GLO Color Swatch's own single-value
  // option swatches) used to load from here, but lazily loading it via
  // this sibling-file injector - which only runs after first paint -
  // caused a visible FOUC: Size: One Size / Color: White would flash
  // visible for an instant before disappearing on every fresh product
  // page load. It's now loaded render-blocking from layout/theme.liquid's
  // <head> instead (Dawn's native stylesheet_tag pattern), available
  // before the browser paints anything - see that file and
  // assets/gcw-hide-one-variant.css for the full rationale.
})();
